import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ReferenceDefaultValues() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📖 CSS Reference: Default Values</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Every CSS property has a <b>default (initial) value</b> that applies if
        no value is set by the author or inherited from a parent. Knowing these
        defaults helps you write cleaner CSS and avoid redundant rules.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #f3e8ff, #e9d5ff)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#6b21a8" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            The <code>initial</code> keyword can reset any property to its
            default.
          </li>
          <li>
            Defaults differ by property: some are <code>0</code>, some{" "}
            <code>auto</code>, others <code>none</code>.
          </li>
          <li>
            Not all properties are inherited — e.g. <code>color</code> inherits,
            <code>margin</code> does not.
          </li>
          <li>Use browser dev tools to inspect computed styles if unsure.</li>
        </ul>
      </div>

      {/* Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">📝 Common Default Values</h2>
        <CodeCopyBlock
          code={`/* Examples of common CSS property defaults */
div {
  display: block;        /* block-level elements */
  margin: 0;             /* no margin by default */
  padding: 0;            /* no padding by default */
  border: none;          /* no border */
  font-size: medium;     /* typically 16px */
  font-weight: normal;   /* not bold */
  color: initial;        /* browser default text color */
  background-color: transparent;
  position: static;      /* normal document flow */
  text-align: start;     /* left in LTR, right in RTL */
  visibility: visible;   /* visible unless hidden */
}`}
        />

        <div
          style={{
            marginTop: "1rem",
            padding: "1rem",
            backgroundColor: "#f1f5f9",
            borderRadius: "8px",
          }}
        >
          <p>
            <b>Example:</b> A <code>&lt;div&gt;</code> without custom styles is
            <b> block-level</b>, has <b>no margins or padding</b>,{" "}
            <b>transparent background</b>, and <b>normal font</b>.
          </p>
          <div
            style={{
              border: "1px dashed #94a3b8",
              padding: "10px",
              marginTop: "8px",
            }}
          >
            Default-styled div
          </div>
        </div>
      </div>

      {/* Table of Default Values */}
      <div className="tutorial-section">
        <h2 className="section-title">📊 Quick Reference Table</h2>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginTop: "1rem",
            fontSize: "15px",
          }}
        >
          <thead>
            <tr style={{ background: "#e2e8f0" }}>
              <th style={{ border: "1px solid #cbd5e1", padding: "8px" }}>
                Property
              </th>
              <th style={{ border: "1px solid #cbd5e1", padding: "8px" }}>
                Default Value
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                display
              </td>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                inline (for span), block (for div)
              </td>
            </tr>
            <tr>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                position
              </td>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                static
              </td>
            </tr>
            <tr>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                font-size
              </td>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                medium (~16px)
              </td>
            </tr>
            <tr>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                font-weight
              </td>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                normal (400)
              </td>
            </tr>
            <tr>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                line-height
              </td>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                normal
              </td>
            </tr>
            <tr>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                margin
              </td>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>0</td>
            </tr>
            <tr>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                padding
              </td>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>0</td>
            </tr>
            <tr>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                background-color
              </td>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                transparent
              </td>
            </tr>
            <tr>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                color
              </td>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                depends on browser (usually black)
              </td>
            </tr>
            <tr>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                visibility
              </td>
              <td style={{ border: "1px solid #cbd5e1", padding: "6px" }}>
                visible
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #ffedd5, #fed7aa)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#9a3412" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn the difference between <b>initial</b>, <b>inherit</b>, and{" "}
            <b>unset</b>.
          </li>
          <li>Inspect elements in browser DevTools to see computed styles.</li>
          <li>Use CSS resets or normalize.css to standardize defaults.</li>
          <li>Check MDN docs for property-specific default values.</li>
        </ul>
      </div>
    </div>
  );
}
